import { useState, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star } from "lucide-react";
import type { OperationsTask, VendorReview } from "@shared/schema";

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="focus:outline-none"
          data-testid={`review-star-${star}`}
        >
          <Star
            className={`h-6 w-6 transition-colors ${
              star <= value ? "text-yellow-400 fill-yellow-400" : "text-gray-200 hover:text-yellow-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export function LeaveReviewModal({
  task,
  open,
  onClose,
}: {
  task: (OperationsTask & { vendorName?: string | null }) | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [overallRating, setOverallRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [timelinessRating, setTimelinessRating] = useState(0);
  const [professionalismRating, setProfessionalismRating] = useState(0);
  const [showCategoryRatings, setShowCategoryRatings] = useState(false);
  const [publicComment, setPublicComment] = useState("");
  const [privateNote, setPrivateNote] = useState("");
  const [shareHostName, setShareHostName] = useState(false);

  const existingQuery = useQuery<{ review: VendorReview | null }>({
    queryKey: ["/api/app/operations/approvals", task?.id, "review"],
    enabled: open && !!task?.id,
  });

  useEffect(() => {
    if (!open || !task?.id) return;
    const r = existingQuery.data?.review;
    setOverallRating(r?.overallRating ?? 0);
    setQualityRating(r?.qualityRating ?? 0);
    setCommunicationRating(r?.communicationRating ?? 0);
    setTimelinessRating(r?.timelinessRating ?? 0);
    setProfessionalismRating(r?.professionalismRating ?? 0);
    setPublicComment(r?.publicComment ?? "");
    setPrivateNote(r?.privateNote ?? "");
    setShareHostName(!!r?.shareHostName);
    setShowCategoryRatings(
      !!(r?.qualityRating || r?.communicationRating || r?.timelinessRating || r?.professionalismRating),
    );
  }, [open, task?.id, existingQuery.data]);

  const resetForm = () => {
    setOverallRating(0);
    setQualityRating(0);
    setCommunicationRating(0);
    setTimelinessRating(0);
    setProfessionalismRating(0);
    setShowCategoryRatings(false);
    setPublicComment("");
    setPrivateNote("");
    setShareHostName(false);
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!task) throw new Error("No task selected");
      const res = await apiRequest("POST", `/api/app/operations/approvals/${task.id}/review`, {
        overallRating,
        qualityRating: qualityRating || null,
        communicationRating: communicationRating || null,
        timelinessRating: timelinessRating || null,
        professionalismRating: professionalismRating || null,
        publicComment: publicComment || null,
        privateNote: privateNote || null,
        shareHostName,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/approvals", task?.id, "review"] });
      if (task?.vendorId) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors", task.vendorId, "reviews"] });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
      onClose();
      resetForm();
      toast({ title: "Review saved", description: "Your review is now on the vendor's profile." });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't save review", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  const lowRatingNeedsNote = overallRating > 0 && overallRating < 2 && !privateNote;
  const canSubmit = overallRating > 0 && !lowRatingNeedsNote && !submitMutation.isPending;

  const handleOpenChange = (next: boolean) => {
    if (submitMutation.isPending) return;
    if (!next) onClose();
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        onInteractOutside={(e) => { if (submitMutation.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (submitMutation.isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (submitMutation.isPending) e.preventDefault(); }}
        data-testid="dialog-leave-review"
      >
        <DialogHeader>
          <DialogTitle>Leave a review{task.vendorName ? ` for ${task.vendorName}` : ""}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">Rate this job</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Overall:</span>
                <StarRating value={overallRating} onChange={setOverallRating} />
              </div>
              <button
                type="button"
                onClick={() => setShowCategoryRatings(!showCategoryRatings)}
                className="text-xs text-blue-600 hover:text-blue-700"
                data-testid="button-review-toggle-categories"
              >
                {showCategoryRatings ? "Hide" : "Show"} category ratings ▼
              </button>
              {showCategoryRatings && (
                <div className="space-y-2 pl-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Quality:</span>
                    <StarRating value={qualityRating} onChange={setQualityRating} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Communication:</span>
                    <StarRating value={communicationRating} onChange={setCommunicationRating} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Timeliness:</span>
                    <StarRating value={timelinessRating} onChange={setTimelinessRating} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Professionalism:</span>
                    <StarRating value={professionalismRating} onChange={setProfessionalismRating} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-600 block mb-1">Comment (optional, public on vendor profile):</label>
            <Textarea
              value={publicComment}
              onChange={(e) => setPublicComment(e.target.value)}
              rows={3}
              data-testid="textarea-review-public-comment"
            />
          </div>

          {overallRating > 0 && overallRating < 2 && (
            <div>
              <label className="text-sm text-red-600 block mb-1">Private note (required for low ratings):</label>
              <Textarea
                value={privateNote}
                onChange={(e) => setPrivateNote(e.target.value)}
                rows={2}
                data-testid="textarea-review-private-note"
              />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={shareHostName} onCheckedChange={(v) => setShareHostName(!!v)} />
            Share my name with this review
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} disabled={submitMutation.isPending}>Cancel</Button>
            <Button
              className="bg-brand hover:bg-brand-hover"
              onClick={() => submitMutation.mutate()}
              disabled={!canSubmit}
              data-testid="button-submit-review"
            >
              {submitMutation.isPending ? "Saving..." : "Save review"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
